import { OnlineCalendarOrder } from '../../models/serializable/online-calendar-order.js';
import { OnlineEventPassOrder } from '../../models/serializable/online-event-pass-order.js';
import { OnlineEventTicketOrder } from '../../models/serializable/online-event-ticket-order.js';
import { OnlineProductOrder } from '../../models/serializable/online-product-order.js';
import { Price } from '../../models/serializable/price.js';

export abstract class IOrderPriceBreakdown {
  abstract get orderTypeId(): string;
  abstract get itemPrice(): Price;
  abstract get totalPrice(): Price;
  abstract get donationPrice(): Price | null;
  abstract get standardFeePrice(): Price | null;
  abstract get totalFeePrice(): Price | null;
}

export class BasicOrderPriceBreakdown extends IOrderPriceBreakdown {
  private constructor(
      public readonly orderTypeId: string,
      public readonly itemPrice: Price,
      public readonly donationPrice: Price | null,
      public readonly standardFeePrice: Price | null,
      public readonly totalFeePrice: Price | null,
      public readonly totalPrice: Price,
  ) {
    super();
  }

  static product(args: {
      itemPrice: Price,
      donationPrice: Price | null,
      standardFeePrice: Price | null,
      totalFeePrice: Price | null,
      totalPrice: Price,
  }): BasicOrderPriceBreakdown {
    return new BasicOrderPriceBreakdown(
        OnlineProductOrder.TYPE_ID,
        args.itemPrice,
        args.donationPrice,
        args.standardFeePrice,
        args.totalFeePrice,
        args.totalPrice,
    );
  }

  static pass(args: {
      itemPrice: Price,
      donationPrice: Price | null,
      standardFeePrice: Price | null,
      totalFeePrice: Price | null,
      totalPrice: Price,
  }): BasicOrderPriceBreakdown {
    return new BasicOrderPriceBreakdown(
        OnlineEventPassOrder.TYPE_ID,
        args.itemPrice,
        args.donationPrice,
        args.standardFeePrice,
        args.totalFeePrice,
        args.totalPrice,
    );
  }

  static calendar(args: {
      itemPrice: Price,
      donationPrice: Price | null,
      standardFeePrice: Price | null,
      totalFeePrice: Price | null,
      totalPrice: Price,
  }): BasicOrderPriceBreakdown {
    return new BasicOrderPriceBreakdown(
        OnlineCalendarOrder.TYPE_ID,
        args.itemPrice,
        args.donationPrice,
        args.standardFeePrice,
        args.totalFeePrice,
        args.totalPrice,
    );
  }

  static verifyType(dm: IOrderPriceBreakdown): boolean {
    return dm instanceof BasicOrderPriceBreakdown;
  }

  static assertType(dm: IOrderPriceBreakdown): dm is BasicOrderPriceBreakdown {
    return dm instanceof BasicOrderPriceBreakdown;
  }
}

export class OnlineTicketOrderPriceBreakdown extends IOrderPriceBreakdown {
  public readonly orderTypeId = OnlineEventTicketOrder.TYPE_ID;

  private constructor(
      public readonly itemPrice: Price,
      public readonly productPrice: Price | null,
      public readonly supplementPrice: Price | null,
      public readonly donationPrice: Price | null,
      public readonly standardFeePrice: Price | null,
      public readonly productFeePrice: Price | null,
      public readonly totalFeePrice: Price | null,
      public readonly totalPrice: Price,
  ) {
    super();
  }

  static create(args: {
      itemPrice: Price,
      productPrice: Price | null,
      supplementPrice: Price | null,
      donationPrice: Price | null,
      standardFeePrice: Price | null,
      productFeePrice: Price | null,
      totalFeePrice: Price | null,
      totalPrice: Price,
  }): OnlineTicketOrderPriceBreakdown {
    return new OnlineTicketOrderPriceBreakdown(
        args.itemPrice,
        args.productPrice,
        args.supplementPrice,
        args.donationPrice,
        args.standardFeePrice,
        args.productFeePrice,
        args.totalFeePrice,
        args.totalPrice,
    );
  }

  static verifyType(dm: IOrderPriceBreakdown): boolean {
    return dm instanceof OnlineTicketOrderPriceBreakdown;
  }

  static assertType(dm: IOrderPriceBreakdown): dm is OnlineTicketOrderPriceBreakdown {
    return dm instanceof OnlineTicketOrderPriceBreakdown;
  }
}
